// SPDX-License-Identifier: AGPL-3.0-or-later
// Based on au-births by Ben Richardson — https://benrichardson.dev
// The eight views. Each render function fills a container and wires its own
// interactions. Kept in one module so shared building blocks stay DRY.
import {
  type BirthsData, type Meta, REGION_ORDER, REGION_COLOUR, AGE_COLOUR,
  fmt, fmtInt, fmtPct, fmtSigned, lastVal, firstVal, exNuptialShare, esc,
} from './data';
import {
  lineChart, barChart, hBars, stackedArea, heatmap, histogram,
  warmRamp, tealRamp, type LineSeries, type HeatCell,
} from './charts';
import { attachSvgZoom } from './utils/svgZoom';
import { renderMap, MAP_MODES, type MapMode, type MapController } from './map';
import { buildInsights } from './analysis';
import { glossLink } from './glossary';

export interface Ctx { data: BirthsData; meta: Meta; openState: (code: string) => void }

// ── Shared building blocks ─────────────────────────────────────────────────
function tile(label: string, value: string, sub = ''): string {
  return `<div class="tile"><div class="tile-val">${value}</div><div class="tile-lab">${label}</div>${sub ? `<div class="tile-sub">${sub}</div>` : ''}</div>`;
}
function head(title: string, sub: string): string {
  return `<div class="view-head"><h2>${title}</h2><p>${sub}</p></div>`;
}
function card(title: string, sub: string, body: string, foot = ''): string {
  return `<section class="card"><div class="card-head"><h3>${title}</h3>${sub ? `<p>${sub}</p>` : ''}</div><div class="card-body">${body}</div>${foot ? `<div class="card-foot">${foot}</div>` : ''}</section>`;
}
const zoomable = (svg: string) => `<div class="zoom-wrap">${svg}</div>`;

function decadeAnnotations(): { x: number; label: string }[] {
  return [
    { x: 2000, label: 'GST' },
    { x: 2008, label: 'GFC bump' },
    { x: 2020, label: 'COVID' },
  ];
}

// ── 1. Overview ─────────────────────────────────────────────────────────────
export function renderOverview(root: HTMLElement, ctx: Ctx): void {
  const { data, meta } = ctx;
  const aus = data.summary.AUS;
  const exShare = exNuptialShare(aus);

  const tfrLine = lineChart({
    years: data.years,
    series: [{ label: 'Total fertility rate', colour: REGION_COLOUR.AUS, values: aus.tfr, width: 2.8 }],
    yMin: 1.3, yMax: 3.0, unit: '', dp: 3, yLabel: 'babies per woman',
    refLine: { y: 2.1, label: 'replacement rate 2.1' },
    annotations: decadeAnnotations(),
  });
  const birthsBars = barChart(
    data.years.map((y, i) => ({ label: String(y), value: aus.births[i] ?? 0, colour: '#5eead4', tip: `${y}\n${fmtInt(aus.births[i])} births` })),
    { yLabel: 'births registered', unit: '', dp: 0 },
  );
  const ageLine = lineChart({
    years: data.years,
    series: [
      { label: 'Median age of mother', colour: '#be185d', values: aus.medMother, width: 2.6 },
      { label: 'Median age of father', colour: '#7c3aed', values: aus.medFather, width: 2 },
      { label: 'First-time mothers', colour: '#0d9488', values: aus.medFirstMother, dash: true, width: 2 },
    ],
    yMin: 24, yMax: 36, unit: ' yrs', dp: 1, yLabel: 'age (years)',
  });

  root.innerHTML =
    head('Australia is having fewer children, much later',
      `Fifty years of ABS birth registrations, ${meta.dataFrom}–${meta.dataThrough}. The ${glossLink('tfr', 'fertility rate')} has fallen to a record low, below the ${glossLink('replacement', 'level needed to replace the population')}, while parents keep getting older.`) +
    `<div class="hero-grid">
       <div class="hero-lead">
         <div class="hero-big">${fmt(meta.tfr.latest, 2)}</div>
         <div class="hero-cap">babies per woman in ${meta.dataThrough} — a record low, down from ${fmt(meta.tfr.first, 2)} in ${meta.dataFrom} and below the 2.1 replacement rate</div>
       </div>
       <div class="tiles">
         ${tile('Births in ' + meta.dataThrough, fmtInt(meta.births.latest), `peak ${fmtInt(meta.births.peak)} in ${meta.births.peakYear}`)}
         ${tile('Median age of mother', fmt(meta.medianAgeMother.latest, 1) + ' yrs', `up ${fmt(meta.medianAgeMother.latest - meta.medianAgeMother.first, 1)} yrs since ${meta.dataFrom}`)}
         ${tile('Born outside marriage', fmtPct(lastVal(exShare), 0), `was ${fmtPct(firstVal(exShare), 0)} in ${meta.dataFrom}`)}
         ${tile('Below replacement since', meta.tfr.belowSince ? String(meta.tfr.belowSince) : '—', meta.tfr.belowSince ? `${meta.dataThrough - meta.tfr.belowSince} years and counting` : '')}
       </div>
     </div>` +
    card('The fertility rate has never been lower', 'Total fertility rate for Australia, with the replacement rate of 2.1 marked. It fell below replacement in the mid-1970s and has stayed there.', tfrLine) +
    `<div class="grid-2">
       ${card('More people, fewer babies each', 'Total births registered per year. The count holds up because the population keeps growing, even as births per woman fall.', birthsBars)}
       ${card('Parents keep getting older', 'Median age of mothers and fathers at birth, and of first-time mothers.', ageLine)}
     </div>`;

  wireZoom(root);
}

// ── 2. Fertility rate ───────────────────────────────────────────────────────
export function renderFertility(root: HTMLElement, ctx: Ctx): void {
  const { data, meta } = ctx;
  root.innerHTML =
    head('The fertility rate', `How many children the average woman would have at today's rates. Below the ${glossLink('replacement', 'replacement rate')} of 2.1, a population shrinks without migration.`) +
    `<div class="seg" data-seg="scope">
       <button data-val="nat" class="active">Australia</button>
       <button data-val="state">By state</button>
     </div>
     <div id="fert-main"></div>` +
    card('Which states are above or below replacement?',
      `Total fertility rate by state in ${meta.dataThrough}. None is above the 2.1 replacement line; bars are greener the closer they get, redder the further below.`,
      stateReplacementBars(data)) +
    card('Every state is falling', 'Total fertility rate by state over the full record. Click a state on the map view for its full profile.', stateTfrLines(data));

  const main = root.querySelector('#fert-main') as HTMLElement;
  const drawNat = () => {
    main.innerHTML = card('Total fertility rate, Australia',
      'The national fertility rate since 1975 with the replacement rate marked and key moments annotated.',
      lineChart({
        years: data.years,
        series: [{ label: 'Fertility rate', colour: REGION_COLOUR.AUS, values: data.summary.AUS.tfr, width: 2.8 }],
        yMin: 1.3, yMax: 3.0, dp: 3, yLabel: 'babies per woman',
        refLine: { y: 2.1, label: 'replacement rate 2.1' }, annotations: decadeAnnotations(),
      }));
    wireZoom(main);
  };
  const drawState = () => { main.innerHTML = card('Total fertility rate by state', 'All eight states and territories since 1975.', stateTfrLines(data)); wireZoom(main); };
  drawNat();
  segControl(root, 'scope', (v) => (v === 'nat' ? drawNat() : drawState()));
  wireZoom(root);
}

function stateTfrLines(data: BirthsData): string {
  const series: LineSeries[] = REGION_ORDER.map((c) => ({ label: data.regionNames[c], colour: REGION_COLOUR[c], values: data.summary[c].tfr, width: 1.8 }));
  return zoomable(lineChart({ years: data.years, series, yMin: 1.0, yMax: 3.2, dp: 3, yLabel: 'babies per woman', refLine: { y: 2.1, label: 'replacement 2.1' }, markers: false })) +
    legend(REGION_ORDER.map((c) => ({ label: data.regionNames[c], colour: REGION_COLOUR[c] })));
}

function stateReplacementBars(data: BirthsData): string {
  const rows = REGION_ORDER.map((c) => ({ code: c, v: lastVal(data.summary[c].tfr) })).filter((r) => r.v != null) as { code: string; v: number }[];
  rows.sort((a, b) => b.v - a.v);
  return hBars(rows.map((r) => {
    const t = Math.max(0, Math.min(1, (r.v - 1.2) / (2.1 - 1.2)));
    return { label: data.regionNames[r.code], value: r.v, colour: warmToGreen(t), id: r.code, tip: `${data.regionNames[r.code]}\nTFR ${fmt(r.v, 3)} (${fmt((2.1 - r.v), 2)} below replacement)` };
  }), { dp: 3, ref: { value: 2.1, label: 'replacement 2.1' } });
}
function warmToGreen(t: number): string {
  // red (far below) → amber → green (near replacement)
  return t < 0.5 ? `rgb(${Math.round(220 - (220 - 217) * t * 2)},${Math.round(38 + (119 - 38) * t * 2)},${Math.round(38 + (6 - 38) * t * 2)})` : `rgb(${Math.round(217 - (217 - 22) * (t - 0.5) * 2)},${Math.round(119 + (163 - 119) * (t - 0.5) * 2)},${Math.round(6 + (74 - 6) * (t - 0.5) * 2)})`;
}

// ── 3. Older mothers (ASFR surface) ─────────────────────────────────────────
export function renderAges(root: HTMLElement, ctx: Ctx): void {
  const { data, meta } = ctx;
  root.innerHTML =
    head('The shift to older mothers',
      `The ${glossLink('asfr', 'age-specific fertility rate')} — births per 1,000 women of each age group. The bright band of peak childbearing has slid from the early 20s to the early 30s.`) +
    card('When Australians have children, 1975–' + meta.dataThrough,
      'Each cell is the fertility rate for an age group in a year — brighter means more births per 1,000 women. Watch the peak move down the ages. Scroll to zoom, drag to pan.',
      zoomable(asfrSurface(data)) + rampLegend('Births per 1,000 women', warmRamp, asfrExtent(data))) +
    `<div class="seg-row"><label class="seg-label">Compare an age profile:</label><div class="seg wrap" data-seg="year">${data.years.filter((y) => y % 5 === 0 || y === meta.dataThrough).map((y) => `<button data-val="${y}"${y === meta.dataThrough ? ' class="active"' : ''}>${y}</button>`).join('')}</div></div>
     <div id="age-profile"></div>` +
    card('Median age at birth', 'Half of parents are younger than this, half older.', lineChart({
      years: data.years,
      series: [
        { label: 'Mother', colour: '#be185d', values: data.summary.AUS.medMother, width: 2.6 },
        { label: 'First-time mother', colour: '#0d9488', values: data.summary.AUS.medFirstMother, dash: true, width: 2 },
        { label: 'Father', colour: '#7c3aed', values: data.summary.AUS.medFather, width: 2 },
      ], yMin: 24, yMax: 36, unit: ' yrs', dp: 1, yLabel: 'age (years)',
    }) + legend([{ label: 'Mother', colour: '#be185d' }, { label: 'First-time mother', colour: '#0d9488' }, { label: 'Father', colour: '#7c3aed' }]));

  const profile = root.querySelector('#age-profile') as HTMLElement;
  const draw = (year: number) => { profile.innerHTML = card(`Age profile: ${year} vs ${meta.dataFrom}`, 'Fertility rate by age group. The faint bars are ' + meta.dataFrom + ', the solid bars the selected year — the peak has moved right.', ageProfileBars(data, year)); };
  draw(meta.dataThrough);
  segControl(root, 'year', (v) => draw(Number(v)));
  wireZoom(root);
}

function asfrExtent(data: BirthsData): [number, number] {
  const vals: number[] = [];
  for (const g of data.ageGroups) for (const v of data.asfr.AUS[g.id] ?? []) if (v != null) vals.push(v);
  return [0, Math.max(1, ...vals)];
}
function asfrSurface(data: BirthsData): string {
  const groups = [...data.ageGroups].reverse(); // youngest at bottom
  const cells: HeatCell[] = [];
  groups.forEach((g, r) => {
    data.years.forEach((y, c) => {
      const v = data.asfr.AUS[g.id]?.[c] ?? null;
      cells.push({ row: r, col: c, value: v, tip: `${g.label} · ${y}\n${v == null ? '—' : fmt(v, 1) + ' per 1,000 women'}` });
    });
  });
  return heatmap({
    rows: groups.length, cols: data.years.length,
    rowLabels: groups.map((g) => g.label), colLabels: data.years.map(String),
    cells, ramp: warmRamp, vMin: 0, vMax: asfrExtent(data)[1], cellH: 30,
    colLabelStep: Math.ceil(data.years.length / 10),
  });
}
function ageProfileBars(data: BirthsData, year: number): string {
  const i = data.years.indexOf(year);
  const i0 = 0;
  const cur = data.ageGroups.map((g) => data.asfr.AUS[g.id]?.[i] ?? 0);
  const base = data.ageGroups.map((g) => data.asfr.AUS[g.id]?.[i0] ?? 0);
  const max = Math.max(...cur, ...base, 1);
  const W = 900, H = 300, m = { t: 16, r: 16, b: 40, l: 48 };
  const iw = W - m.l - m.r, ih = H - m.t - m.b;
  const step = iw / data.ageGroups.length;
  const bw = step * 0.5;
  let g = '';
  for (let k = 0; k <= 4; k++) { const val = (max / 4) * k; const y = m.t + ih - (val / max) * ih; g += `<line class="grid" x1="${m.l}" y1="${y.toFixed(1)}" x2="${m.l + iw}" y2="${y.toFixed(1)}"/><text class="axlabel" x="${m.l - 6}" y="${(y + 3).toFixed(1)}" text-anchor="end">${Math.round(val)}</text>`; }
  data.ageGroups.forEach((grp, k) => {
    const x = m.l + k * step + (step - bw) / 2;
    const hb = (base[k] / max) * ih; const hc = (cur[k] / max) * ih;
    g += `<rect x="${x.toFixed(1)}" y="${(m.t + ih - hb).toFixed(1)}" width="${bw.toFixed(1)}" height="${hb.toFixed(1)}" fill="#e5e7eb" data-tip="${grp.label} · ${data.years[i0]}&#10;${fmt(base[k], 1)} per 1,000"/>`;
    g += `<rect x="${(x + bw * 0.28).toFixed(1)}" y="${(m.t + ih - hc).toFixed(1)}" width="${bw.toFixed(1)}" height="${hc.toFixed(1)}" fill="${AGE_COLOUR[grp.id]}" rx="2" data-tip="${grp.label} · ${year}&#10;${fmt(cur[k], 1)} per 1,000"/>`;
    g += `<text class="axlabel" x="${(x + bw / 2).toFixed(1)}" y="${H - m.b + 18}" text-anchor="middle">${grp.label}</text>`;
  });
  return `<svg viewBox="0 0 ${W} ${H}" class="chart" preserveAspectRatio="xMidYMid meet" role="img">${g}</svg>` +
    legend([{ label: String(data.years[i0]), colour: '#e5e7eb' }, { label: String(year), colour: '#be185d' }]);
}

// ── 4. Map ──────────────────────────────────────────────────────────────────
let mapCtl: MapController | null = null;
export function renderMapView(root: HTMLElement, ctx: Ctx): void {
  const { data, meta } = ctx;
  root.innerHTML =
    head('Births by state',
      `Where fertility is highest and lowest across Australia, ${meta.dataThrough}. Click any state for its full profile.`) +
    `<div class="seg wrap" data-seg="mode">${MAP_MODES.map((m, i) => `<button data-val="${m.id}"${i === 0 ? ' class="active"' : ''}>${m.label}</button>`).join('')}</div>
     <div class="map-frame"><div id="birth-map"></div></div>
     <p class="map-note">Northern Territory consistently has the highest fertility (younger population, high Indigenous fertility); the ACT and Victoria the lowest. Hover a state for the value, click to drill in.</p>`;
  const host = root.querySelector('#birth-map') as HTMLElement;
  if (mapCtl) { mapCtl.destroy(); mapCtl = null; }
  renderMap(host, data, 'tfr', (code) => ctx.openState(code)).then((c) => { mapCtl = c; });
  segControl(root, 'mode', (v) => mapCtl?.setMode(v as MapMode));
}
export function teardownMap(): void { if (mapCtl) { mapCtl.destroy(); mapCtl = null; } }

// ── 5. Nuptiality ───────────────────────────────────────────────────────────
export function renderNuptiality(root: HTMLElement, ctx: Ctx): void {
  const { data, meta } = ctx;
  const aus = data.summary.AUS;
  const exShare = exNuptialShare(aus);
  root.innerHTML =
    head('Marriage and birth',
      `Half a century ago almost every baby was born to married parents. Today ${fmtPct(lastVal(exShare), 0)} are ${glossLink('exnuptial', 'born outside marriage')} — though most are still registered by both parents.`) +
    `<div class="tiles">
       ${tile('Born outside marriage, ' + meta.dataThrough, fmtPct(lastVal(exShare), 1), `up from ${fmtPct(firstVal(exShare), 1)} in ${meta.dataFrom}`)}
       ${tile('Median marriage length at birth', fmt(lastVal(aus.medDuration), 1) + ' yrs', 'for babies born to married parents')}
       ${tile('Nuptial births, ' + meta.dataThrough, fmtInt(lastVal(aus.nuptial)), 'to married parents')}
       ${tile('Ex-nuptial births, ' + meta.dataThrough, fmtInt(lastVal(aus.exNuptial)), 'to unmarried parents')}
     </div>
     <div class="seg" data-seg="stack"><button data-val="pct" class="active">Share</button><button data-val="abs">Number</button></div>
     <div id="nupt-stack"></div>` +
    card('Ever more babies born outside marriage', 'Share of births to unmarried parents, by state.', exShareLines(data));

  const stackHost = root.querySelector('#nupt-stack') as HTMLElement;
  const series = (): LineSeries[] => [
    { label: 'Nuptial (married parents)', colour: '#0d9488', values: aus.nuptial },
    { label: 'Ex-nuptial (unmarried parents)', colour: '#e11d48', values: aus.exNuptial },
  ];
  const drawStack = (pct: boolean) => {
    stackHost.innerHTML = card(pct ? 'Nuptial vs ex-nuptial births (share)' : 'Nuptial vs ex-nuptial births (number)',
      'Australian births split by whether the parents were married at the time.',
      stackedArea(data.years, series(), { percent: pct, unit: pct ? '%' : ' births', dp: 0, yLabel: pct ? 'share of births' : 'births' }) +
      legend([{ label: 'Nuptial', colour: '#0d9488' }, { label: 'Ex-nuptial', colour: '#e11d48' }]));
  };
  drawStack(true);
  segControl(root, 'stack', (v) => drawStack(v === 'pct'));
  wireZoom(root);
}
function exShareLines(data: BirthsData): string {
  const series: LineSeries[] = REGION_ORDER.map((c) => ({ label: data.regionNames[c], colour: REGION_COLOUR[c], values: exNuptialShare(data.summary[c]), width: 1.8 }));
  return zoomable(lineChart({ years: data.years, series, yMin: 0, yMax: 90, unit: '%', dp: 1, yLabel: 'share born outside marriage', markers: false })) +
    legend(REGION_ORDER.map((c) => ({ label: data.regionNames[c], colour: REGION_COLOUR[c] })));
}

// ── 6. Seasonality ──────────────────────────────────────────────────────────
export function renderSeasonality(root: HTMLElement, ctx: Ctx): void {
  const { data } = ctx;
  const completeIdx = data.years.map((_, i) => i).filter((i) => data.seasonComplete[i]);
  // Average month share across complete years (AUS).
  const monthTotals = new Array(12).fill(0);
  let grand = 0;
  for (const i of completeIdx) {
    const grid = data.season.AUS[i];
    if (!grid) continue;
    grid.forEach((v, m) => { if (v != null) { monthTotals[m] += v; grand += v; } });
  }
  const avgShare = monthTotals.map((t) => (grand > 0 ? (t / grand) * 100 : 0));
  const peakM = avgShare.indexOf(Math.max(...avgShare));
  const lowM = avgShare.indexOf(Math.min(...avgShare));

  root.innerHTML =
    head('When are babies born?',
      `Births by ${glossLink('occurrence', 'month of occurrence')}, averaged over complete years. Australia has a gentle seasonal rhythm — more babies in ${data.months[peakM]}, fewest in ${data.months[lowM]}. The latest year is left out because recent births are still being registered.`) +
    card('The birth calendar', `Each row is a year, each column a month; brighter means more births. Complete years only (through ${data.lastCompleteSeason}). Scroll to zoom.`,
      zoomable(seasonHeatmap(data, completeIdx)) + rampLegend('Births in month', tealRamp, seasonExtent(data, completeIdx))) +
    card('Which months have the most babies?', 'Average share of the year\'s births falling in each month, across complete years. A perfectly even year would be 8.3% per month (dashed line).', monthProfile(data, avgShare));
  wireZoom(root);
}
function seasonExtent(data: BirthsData, idx: number[]): [number, number] {
  const vals: number[] = [];
  for (const i of idx) for (const v of data.season.AUS[i] ?? []) if (v != null) vals.push(v);
  return [Math.min(...vals), Math.max(...vals)];
}
function seasonHeatmap(data: BirthsData, idx: number[]): string {
  const cells: HeatCell[] = [];
  idx.forEach((yi, r) => {
    for (let m = 0; m < 12; m++) {
      const v = data.season.AUS[yi]?.[m] ?? null;
      cells.push({ row: r, col: m, value: v, tip: `${data.months[m]} ${data.years[yi]}\n${v == null ? '—' : fmtInt(v) + ' births'}` });
    }
  });
  const [lo, hi] = seasonExtent(data, idx);
  return heatmap({
    rows: idx.length, cols: 12, rowLabels: idx.map((i) => String(data.years[i])), colLabels: data.months,
    cells, ramp: tealRamp, vMin: lo, vMax: hi, cellH: 15, colLabelStep: 1,
  });
}
function monthProfile(data: BirthsData, avgShare: number[]): string {
  return barChart(data.months.map((mn, m) => ({ label: mn, value: avgShare[m], colour: '#0d9488', tip: `${mn}\n${fmt(avgShare[m], 2)}% of annual births` })), { yLabel: 'share of births (%)', unit: '%', dp: 2, height: 300 }).replace('</svg>',
    // add the even 8.33% reference line
    (() => { const H = 300, m = { t: 20, b: 46, l: 56, r: 16 }; const ih = H - m.t - m.b; const iw = 900 - m.l - m.r; const max = Math.max(...avgShare, 8.34); const y = m.t + ih - (8.3333 / max) * ih; return `<line x1="${m.l}" y1="${y.toFixed(1)}" x2="${m.l + iw}" y2="${y.toFixed(1)}" stroke="#94a3b8" stroke-dasharray="5 4" stroke-width="1.2"/><text x="${m.l + iw - 4}" y="${(y - 5).toFixed(1)}" text-anchor="end" class="refline-label">even 8.3%</text>`; })() + '</svg>');
}

// ── 7. Explorer ─────────────────────────────────────────────────────────────
interface ExpState { search: string; year: string; bin: number | null }
export function renderExplorer(root: HTMLElement, ctx: Ctx): void {
  const { data, meta } = ctx;
  const st: ExpState = { search: '', year: String(meta.dataThrough), bin: null };
  let sortKey: 'region' | 'year' | 'births' | 'tfr' | 'cbr' | 'medMother' | 'exShare' = 'tfr';
  let sortDir: 1 | -1 = 1;

  // Build the full row set (region × year).
  interface Row { code: string; region: string; year: number; births: number | null; tfr: number | null; cbr: number | null; medMother: number | null; exShare: number | null }
  const rows: Row[] = [];
  for (const code of REGION_ORDER) {
    const s = data.summary[code]; const ex = exNuptialShare(s);
    data.years.forEach((y, i) => rows.push({ code, region: data.regionNames[code], year: y, births: s.births[i], tfr: s.tfr[i], cbr: s.cbr[i], medMother: s.medMother[i], exShare: ex[i] }));
  }
  const tfrVals = rows.map((r) => r.tfr).filter((v): v is number => v != null);

  root.innerHTML =
    head('Explore every year and state', 'Search, sort and filter every state-year in the record. Click a fertility-rate bar to filter the table to that band; click a state name for its full profile.') +
    `<div class="controls">
       <input id="exp-search" class="search" type="search" placeholder="Filter by state…" aria-label="Filter by state">
       <label class="ctl">Year
         <select id="exp-year">
           <option value="all">All years</option>
           ${data.years.slice().reverse().map((y) => `<option value="${y}"${y === meta.dataThrough ? ' selected' : ''}>${y}</option>`).join('')}
         </select>
       </label>
       <button id="exp-clear" class="btn-clear" hidden>Clear filter</button>
     </div>
     <div id="exp-hist"></div>
     <div class="table-scroll"><table class="data-table"><thead><tr>
       ${th('region', 'State')}${th('year', 'Year')}${th('births', 'Births')}${th('tfr', 'TFR')}${th('cbr', 'CBR')}${th('medMother', 'Median mother age')}${th('exShare', 'Ex-nuptial %')}
     </tr></thead><tbody id="exp-body"></tbody></table></div>`;

  function th(key: string, label: string): string { return `<th data-sort="${key}" class="sortable">${label}<span class="sort-ind"></span></th>`; }

  const bodyEl = root.querySelector('#exp-body') as HTMLElement;
  const histEl = root.querySelector('#exp-hist') as HTMLElement;
  const clearBtn = root.querySelector('#exp-clear') as HTMLButtonElement;

  function activeBinBounds(): [number, number] | null {
    if (st.bin == null) return null;
    const { binsInfo } = histogram(tfrVals, { bins: 12, domainMin: Math.min(...tfrVals), domainMax: Math.max(...tfrVals) });
    const b = binsInfo[st.bin];
    return b ? [b.x0, b.x1] : null;
  }
  function filtered(): Row[] {
    const q = st.search.trim().toLowerCase();
    const bounds = activeBinBounds();
    let out = rows.filter((r) => {
      if (q && !r.region.toLowerCase().includes(q)) return false;
      if (st.year !== 'all' && r.year !== Number(st.year)) return false;
      if (bounds && (r.tfr == null || r.tfr < bounds[0] || r.tfr > bounds[1] + (st.bin === 11 ? 1e-9 : 0))) return false;
      return true;
    });
    out.sort((a, b) => {
      const av = a[sortKey] as number | string | null; const bv = b[sortKey] as number | string | null;
      if (av == null) return 1; if (bv == null) return -1;
      if (typeof av === 'string') return String(av).localeCompare(String(bv)) * sortDir;
      return ((av as number) - (bv as number)) * sortDir;
    });
    return out;
  }
  function drawHist(): void {
    const { svg } = histogram(tfrVals, { bins: 12, unit: '', dp: 2, activeBin: st.bin, domainMin: Math.min(...tfrVals), domainMax: Math.max(...tfrVals) });
    histEl.innerHTML = card('Distribution of fertility rates', 'Every state-year, binned by fertility rate. Click a bar to filter the table.', svg);
    histEl.querySelectorAll('.histbar').forEach((bar) => bar.addEventListener('click', () => {
      const b = Number((bar as HTMLElement).dataset.bin);
      st.bin = st.bin === b ? null : b;
      clearBtn.hidden = st.bin == null;
      drawHist(); drawBody();
    }));
  }
  function drawBody(): void {
    const rs = filtered();
    bodyEl.innerHTML = rs.map((r) => `<tr>
       <td><button class="link-cell" data-code="${r.code}">${esc(r.region)}</button></td>
       <td class="num">${r.year}</td>
       <td class="num">${fmtInt(r.births)}</td>
       <td class="num">${fmt(r.tfr, 3)}</td>
       <td class="num">${fmt(r.cbr, 1)}</td>
       <td class="num">${fmt(r.medMother, 1)}</td>
       <td class="num">${fmt(r.exShare, 1)}</td>
     </tr>`).join('') || `<tr><td colspan="7" class="empty">No matching records.</td></tr>`;
    bodyEl.querySelectorAll('.link-cell').forEach((el) => el.addEventListener('click', () => ctx.openState((el as HTMLElement).dataset.code!)));
    root.querySelectorAll('th.sortable').forEach((th2) => {
      const k = (th2 as HTMLElement).dataset.sort!;
      th2.classList.toggle('sorted', k === sortKey);
      (th2.querySelector('.sort-ind') as HTMLElement).textContent = k === sortKey ? (sortDir === 1 ? ' ▲' : ' ▼') : '';
    });
  }

  (root.querySelector('#exp-search') as HTMLInputElement).addEventListener('input', debounce((e) => { st.search = (e.target as HTMLInputElement).value; drawBody(); }, 250));
  (root.querySelector('#exp-year') as HTMLSelectElement).addEventListener('change', (e) => { st.year = (e.target as HTMLSelectElement).value; drawBody(); });
  clearBtn.addEventListener('click', () => { st.bin = null; clearBtn.hidden = true; drawHist(); drawBody(); });
  root.querySelectorAll('th.sortable').forEach((th2) => th2.addEventListener('click', () => {
    const k = (th2 as HTMLElement).dataset.sort as typeof sortKey;
    if (k === sortKey) sortDir = (sortDir === 1 ? -1 : 1); else { sortKey = k; sortDir = k === 'region' ? 1 : -1; }
    drawBody();
  }));
  drawHist(); drawBody();
}

// ── 8. Insights ─────────────────────────────────────────────────────────────
export function renderInsights(root: HTMLElement, ctx: Ctx): void {
  const insights = buildInsights(ctx.data, ctx.meta);
  root.innerHTML =
    head('What the data shows', 'Findings pulled automatically from the numbers — the patterns worth knowing at a glance.') +
    `<div class="insight-grid">${insights.map((ins) => `
       <article class="insight sev-${ins.severity}">
         <div class="insight-badge">${ins.severity === 'alert' ? 'Key finding' : ins.severity === 'warn' ? 'Notable' : 'Context'}</div>
         <h3>${ins.title}</h3>
         <p>${ins.detail}</p>
       </article>`).join('')}</div>`;
}

// ── State drawer content ────────────────────────────────────────────────────
export function stateDrawerHtml(data: BirthsData, meta: Meta, code: string): string {
  const s = data.summary[code]; const name = data.regionNames[code];
  const ex = exNuptialShare(s);
  const tfr = lastVal(s.tfr);
  const rankRows = REGION_ORDER.map((c) => ({ c, v: lastVal(data.summary[c].tfr) })).filter((r) => r.v != null).sort((a, b) => (b.v as number) - (a.v as number));
  const rank = rankRows.findIndex((r) => r.c === code) + 1;
  const isNat = code === 'AUS';
  const line = lineChart({
    years: data.years,
    series: [{ label: name, colour: REGION_COLOUR[code] ?? REGION_COLOUR.AUS, values: s.tfr, width: 2.6 }],
    yMin: 1.0, yMax: 3.2, dp: 3, yLabel: 'TFR', refLine: { y: 2.1, label: 'replacement 2.1' }, height: 260,
  });
  return `<div class="drawer-inner">
    <div class="drawer-tag" style="background:${REGION_COLOUR[code] ?? REGION_COLOUR.AUS}"></div>
    <h2>${esc(name)}</h2>
    <div class="drawer-tiles">
      ${tile('Fertility rate', fmt(tfr, 3), isNat ? 'national' : `${ordinal(rank)} highest of ${rankRows.length}`)}
      ${tile('Births ' + meta.dataThrough, fmtInt(lastVal(s.births)), '')}
      ${tile('Crude birth rate', fmt(lastVal(s.cbr), 1), 'per 1,000 people')}
      ${tile('Median mother age', fmt(lastVal(s.medMother), 1) + ' yrs', '')}
      ${tile('Born outside marriage', fmtPct(lastVal(ex), 1), '')}
      ${tile('vs national TFR', fmtSigned((tfr ?? 0) - (lastVal(data.summary.AUS.tfr) ?? 0), 3), `nat. ${fmt(lastVal(data.summary.AUS.tfr), 3)}`)}
    </div>
    <h3>Fertility rate since ${meta.dataFrom}</h3>
    ${line}
    <p class="drawer-note">Fertility rate for ${esc(name)} against the 2.1 replacement line. Source: ABS Births, Australia.</p>
  </div>`;
}
function ordinal(n: number): string { const s = ['th', 'st', 'nd', 'rd']; const v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); }

// ── Shared UI helpers ───────────────────────────────────────────────────────
function legend(items: { label: string; colour: string }[]): string {
  return `<div class="legend">${items.map((i) => `<span class="leg-item"><span class="leg-dot" style="background:${i.colour}"></span>${esc(i.label)}</span>`).join('')}</div>`;
}
function rampLegend(title: string, ramp: (t: number) => string, extent: [number, number]): string {
  let sw = '';
  for (let i = 0; i <= 5; i++) { const t = i / 5; sw += `<span class="ramp-swatch" style="background:${ramp(t)}"></span>`; }
  return `<div class="ramp-legend"><span class="ramp-title">${title}</span><span class="ramp-lo">${fmt(extent[0], 0)}</span><span class="ramp-bar">${sw}</span><span class="ramp-hi">${fmt(extent[1], 0)}</span></div>`;
}
function segControl(root: HTMLElement, name: string, onChange: (val: string) => void): void {
  const seg = root.querySelector(`[data-seg="${name}"]`);
  if (!seg) return;
  seg.querySelectorAll('button').forEach((b) => b.addEventListener('click', () => {
    seg.querySelectorAll('button').forEach((x) => x.classList.remove('active'));
    b.classList.add('active');
    onChange((b as HTMLElement).dataset.val!);
  }));
}
function wireZoom(root: HTMLElement): void {
  root.querySelectorAll('.zoom-wrap > svg').forEach((svg) => {
    const el = svg as SVGSVGElement & { _zoomed?: boolean };
    if (el._zoomed) return;
    // jsdom does not implement SVG geometry (viewBox.baseVal); skip there.
    if (!el.viewBox || !el.viewBox.baseVal) return;
    el._zoomed = true;
    try { attachSvgZoom(el); } catch { /* non-fatal: zoom is an enhancement */ }
  });
}
function debounce<T extends (...a: any[]) => void>(fn: T, ms: number): T {
  let t: number | undefined;
  return ((...a: any[]) => { window.clearTimeout(t); t = window.setTimeout(() => fn(...a), ms); }) as T;
}
